import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://mageeenterprises.com";
const DEFAULT_IMAGE =
  "/home/b2b-women-conference.jpg";
const BRAND = "Magee Enterprises";

/**
 * Per-page SEO tags. Injects <title>, description, canonical,
 * Open Graph, Twitter, and optional JSON-LD structured data.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  keywords,
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${BRAND}` : BRAND;
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && (
        <meta name="robots" content="index,follow,max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        Array.isArray(jsonLd)
          ? jsonLd.map((obj, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(obj)}
              </script>
            ))
          : <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND,
  url: SITE_URL,
  logo:
    "/logos/magee-enterprises-logo.png",
  description:
    "Full-service event & meeting planning concierge and event technology experts. Certified project management for meetings, associations, and productions at scale.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-888-299-2250",
    contactType: "customer service",
    availableLanguage: ["English"],
  },
  sameAs: [],
};

// Home page WebSite schema — powers sitelinks search box in Google
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: BRAND,
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
};

export const PERSON_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Sir Dion Magee",
  alternateName: ["Dion Magee", "Sir Dion"],
  url: `${SITE_URL}/speaker`,
  jobTitle: "Emcee, Facilitator, Certified Meeting Professional & Adjunct Professor",
  worksFor: [
    { "@type": "Organization", name: BRAND, url: SITE_URL },
    { "@type": "Organization", name: "OmniPear" },
    { "@type": "Organization", name: "Clincierge" },
  ],
  affiliation: {
    "@type": "CollegeOrUniversity",
    name: "Temple University",
    department: "Adjunct Faculty — Hospitality & Tourism",
  },
  image: "/leadership/dion-magee-portrait.jpg",
  description:
    "Sir Dion Magee, CHE, CMP, PMP, CMM, CGSP, GRCP, GRCA, CSSBB, CRCMP, HMCC, S CIS, CTD, CTA — an award-winning meeting, event & wedding lifestyle expert and project management professional. Leading corporate and social events / wedding industry expert with 25+ years across meetings, hospitality, tourism, and education. Founder of Magee Enterprises and OMNIPEAR, adjunct professor at Temple University, and a founding contributor to Clincierge patient-logistics platform.",
  hasCredential: [
    "Certified Hospitality Educator (CHE)",
    "Certified Meeting Professional (CMP)",
    "Project Management Professional (PMP)",
    "Certificate in Meeting Management (CMM)",
    "Certified Guest Service Professional (CGSP)",
    "GRC Professional (GRCP)",
    "GRC Auditor (GRCA)",
    "Certified Six Sigma Black Belt (CSSBB)",
    "Certified Risk & Compliance Management Professional (CRCMP)",
    "Healthcare Meeting Compliance Certificate (HMCC)",
    "Sustainable Certified Insights Specialist (S CIS)",
    "Certified Travel Director (CTD)",
    "Certified Trade Association (CTA)",
    "PMI Authorized Training Partner Instructor (ATP-I)",
  ],
  award: [
    "Project Management Institute — New Jersey Chapter, 2020 Project Manager of the Year",
    "Brandon Hall Group Bronze Award — Best Certification Program (OmniPear team, 2023)",
  ],
  knowsAbout: [
    "Meeting & event management",
    "Corporate meetings & conferences",
    "Hospitality education",
    "Life-sciences patient logistics",
    "Project management",
    "Risk & compliance",
    "Trade associations",
    "Sustainable events",
  ],
  sameAs: [
    "https://www.credly.com/badges/e6b203e2-5776-422b-b94b-292302186b6f",
  ],
};

/**
 * Build a BreadcrumbList JSON-LD from an array of [name, path] tuples.
 * Emits proper positional itemListElement entries with absolute URLs.
 */
export function buildBreadcrumbs(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, path], idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  };
}

/**
 * Aggregate reviews block — powers star ratings on the SERP.
 * Values are conservative and reflect the testimonial set on this site.
 */
export const AGGREGATE_RATING_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#service`,
  name: BRAND,
  url: SITE_URL,
  image:
    "/logos/magee-enterprises-logo.png",
  priceRange: "$$$",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "12",
    bestRating: "5",
    worstRating: "1",
  },
  review: [
    {
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      author: {
        "@type": "Person",
        name: "Dr. Tiffani (Braswell) Worthy, PMP",
      },
      reviewBody:
        "Dion was phenomenal through the planning and execution of Day & Zimmermann's Engineering, Construction & Maintenance Conference. He provided consistent updates, meaningful inputs, and effectively managed the budget using highly proficient negotiating skills.",
    },
    {
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      author: { "@type": "Person", name: "Professor Joe Goldblatt" },
      reviewBody:
        "One of the world's most talented event planners and most effective motivational speakers. Sir Dion carries my highest and best recommendation!",
    },
    {
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      author: { "@type": "Person", name: "Tangan Lee" },
      reviewBody:
        "Dion was amazing to work with! It was a sincere pleasure. Thank you for the wonderful professionalism you brought to our partnership.",
    },
  ],
};

/**
 * LocalBusiness — powers Google Business Profile match + local pack ranking.
 * Address is placeholder — replace with a verified street address before
 * submitting to Google Business Profile.
 */
export const LOCAL_BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: BRAND,
  url: SITE_URL,
  telephone: "+1-888-299-2250",
  email: "dion@mageeenterprises.com",
  priceRange: "$$$",
  image:
    "/logos/magee-enterprises-logo.png",
  address: {
    "@type": "PostalAddress",
    addressLocality: "New York",
    addressRegion: "NY",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "AdministrativeArea", name: "Global" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
};

/**
 * Build FAQPage JSON-LD from an array of {q, a} pairs.
 * Attach to Home so Google can render the FAQ rich result.
 */
export function buildFaqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export { SITE_URL, DEFAULT_IMAGE, BRAND };
